<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

import { usePageStore } from '../../stores/page'
import { useImgSrcSet } from '../../composables/use-img-src-set'
import { useFlickitySlider } from '../../composables/use-flickity-slider'

import type { LogoSliderParagraph } from '../../utils/paragraph-schema/paragraphs'

type LogoSliderItem = NonNullable<LogoSliderParagraph['logoSlider']['items']>[number]

const props = defineProps<{
  logoSlider: LogoSliderParagraph['logoSlider'],
  listOrderPosition?: number
}>()

const { brandFromStore } = storeToRefs(usePageStore())

const flickityDiv = ref<HTMLElement | null>(null)
// arrow keys cycle between logos only, they don't advance the slide here
const { sliderControlDescription, initFlickitySlider, keepTabbingFocusInSlider } = useFlickitySlider(flickityDiv, { enableArrowKeySlideChange: false })
const { createImgSrc } = useImgSrcSet()

const headline = computed(() => props.logoSlider?.headline)
const showTitles = computed(() => props.logoSlider?.showTitles)
const flickitySliderId = computed(() => `paragraph-type-logo-slider-flickity-${props.listOrderPosition ?? 0}`)

const items = computed(() =>
  (props.logoSlider?.items ?? []).map(item => ({
    ...item,
    imageUrl: getSrcFromImage(item.image)
  }))
)

function getSrcFromImage (image: LogoSliderItem['image']) {
  if (!image) return

  return createImgSrc({
    image,
    brand: brandFromStore.value,
    type: 'webp',
    setName: 'logoSliderItem'
  }) ?? undefined
}

onMounted(async () => {
  await initFlickitySlider({ friction: 0.5 })
})
</script>

<template>
  <div class="bx-content">
    <h2 v-if="headline"
        class="bx-typo--h2">
      {{ headline }}
    </h2>
    <div :id="flickitySliderId"
         ref="flickityDiv"
         :aria-label="`${headline ? `${headline} Karusell` : 'Logo Karusell'}: ${items.length} Inhalte. ${sliderControlDescription}`"
         class="bx-slider bx-slider--logo-slider bx-typo--logo-slider"
         @keydown.enter="keepTabbingFocusInSlider()">
      <div v-for="(item, index) in items"
           :key="`logo-slider-${listOrderPosition}-cell-${index}`"
           class="bx-slider__cell"
           :data-cell-index="index">
        <a :href="item.url"
           :title="item.name">
          <figure v-if="item.image && item.imageUrl"
                  class="bx-slider__item-image"
                  data-tc="item-image">
            <img :src="item.imageUrl"
                 :alt="`${item.image.imageAltText} Logo`"
                 :title="item.image.imageAltText"
                 :width="item.image.width"
                 :height="item.image.height"
                 loading="lazy">
          </figure>
          <div v-if="showTitles && item.name"
               class="bx-slider__item-name"
               data-tc="item-name">
            {{ item.name }}
          </div>
        </a>
      </div>
    </div>
  </div>
</template>
